import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { getProjectById, getStoredProjects, signOffProject, payProjectMilestone, submitProjectClarification } from './storageService';
import { getActiveUser } from './authService';

const ProjectTracker = () => {
  const { id } = useParams();
  const history = useHistory();

  const [searchId, setSearchId] = useState(id || '');
  const [project, setProject] = useState(null);
  const [legalName, setLegalName] = useState('');
  const [showSignModal, setShowSignModal] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [notification, setNotification] = useState('');
  const [clientClarificationReply, setClientClarificationReply] = useState('');
  const [isSubmittingClarification, setIsSubmittingClarification] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const loadProjectData = (lookupId) => {
    const user = getActiveUser();
    setCurrentUser(user);

    const targetId = lookupId || id;
    if (targetId) {
      const found = getProjectById(targetId);
      setProject(found);
      if (found?.client?.name) {
        setLegalName(found.client.name);
      }
    } else {
      if (user) {
        if (user.role === 'admin') {
          const all = getStoredProjects();
          setAllProjects(all);
          if (all.length > 0) {
            setProject(all[0]);
            if (all[0]?.client?.name) setLegalName(all[0].client.name);
          } else {
            setProject(null);
          }
        } else {
          // Client only sees their own projects!
          const userProjects = getStoredProjects().filter(p => 
            p.client?.email?.toLowerCase() === user.email.toLowerCase() ||
            p.client?.name?.toLowerCase() === user.name.toLowerCase()
          );
          setAllProjects(userProjects);
          if (userProjects.length > 0) {
            setProject(userProjects[0]);
            if (userProjects[0]?.client?.name) setLegalName(userProjects[0].client.name);
          } else {
            setProject(null);
          }
        }
      } else {
        // Not logged in and no ID provided in URL - do not leak any projects!
        setAllProjects([]);
        setProject(null);
      }
    }
  };

  useEffect(() => {
    loadProjectData(id);
    const handleUpdate = () => loadProjectData(id);
    window.addEventListener('projects_updated', handleUpdate);
    return () => window.removeEventListener('projects_updated', handleUpdate);
  }, [id]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchId.trim()) {
      history.push(`/project/${searchId.trim().toUpperCase()}`);
    }
  };

  const handleSignOff = (e) => {
    e.preventDefault();
    if (!project || !legalName.trim()) return;

    const updated = signOffProject(project.id, { legalName: legalName.trim() });
    setProject({ ...updated });
    setShowSignModal(false);
    triggerAlert('🎉 SOW digitally executed! Milestone 1 deposit is now ready for kickoff.');
  };

  const handleSimulatePayment = (milestoneIdx) => {
    if (!project) return;
    const updated = payProjectMilestone(project.id, milestoneIdx, { ref: `STRIPE-PAY-${Date.now()}` });
    setProject({ ...updated });
    triggerAlert('💳 Milestone payment verified! Status updated in real time.');
  };

  const triggerAlert = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 4500);
  };

  const handleDownloadSOW = () => {
    if (!project || !project.proposal) return;
    const sowDocument = {
      title: "STATEMENT OF WORK (SOW) - GURU'S WORKSHOP LLC",
      projectId: project.id,
      executedAt: project.signature?.signedAt || new Date().toISOString(),
      client: project.client,
      scope: project.proposal.executiveSummary,
      deliverables: project.proposal.deliverables,
      durationWeeks: project.proposal.estimatedWeeks,
      totalInvestment: `$${project.proposal.totalEstimate}`,
      paymentMilestones: project.proposal.milestones,
      signatureAudit: project.signature || "Pending Execution"
    };

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(sowDocument, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `${project.id}_SOW_Agreement.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const handleClarificationSubmit = (e) => {
    e.preventDefault();
    if (!project || !clientClarificationReply.trim()) return;
    setIsSubmittingClarification(true);
    const updated = submitProjectClarification(project.id, clientClarificationReply.trim());
    if (updated) {
      setProject({ ...updated });
      setClientClarificationReply('');
      triggerAlert('✓ Clarification submitted to Guru! Status updated back to Under Review.');
    }
    setIsSubmittingClarification(false);
  };

  // Lifecycle steps
  const steps = [
    { key: 'Under Review', label: '1. Intake & Review', icon: '📋' },
    { key: 'Needs More Info', label: '2. Needs Clarification', icon: '⚠️' },
    { key: 'Proposal Ready', label: '3. Proposal & SOW Ready', icon: '✍️' },
    { key: 'Signed & In Production', label: '4. Signed & In Production', icon: '🚀' },
    { key: 'Completed', label: '5. Delivered & Handover', icon: '✅' }
  ];

  const getStepIndex = (status) => {
    switch (status) {
      case 'Under Review': return 0;
      case 'Needs More Info': return 1;
      case 'Proposal Ready': return 2;
      case 'Signed & In Production': return 3;
      case 'Completed': return 4;
      default: return 0;
    }
  };

  const currentStepIdx = project ? getStepIndex(project.status) : 0;

  return (
    <div style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', minHeight: '100vh', paddingTop: '2.5rem', paddingBottom: '6rem' }}>
      <div className="container-custom">
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            backgroundColor: '#059669',
            color: '#ffffff',
            padding: '1rem 1.6rem',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(5, 150, 105, 0.3)',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem'
          }}>
            {notification}
          </div>
        )}

        {/* Top Header & Search Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.2rem', marginBottom: '2.5rem' }}>
          <div>
            <div className="pill-badge pill-blue" style={{ marginBottom: '0.5rem' }}>
              PROJECT LIFECYCLE &amp; SOW PORTAL
            </div>
            <h1 style={{ fontSize: 'clamp(2rem, 3.8vw, 2.6rem)', margin: 0, fontWeight: 900 }}>
              What It Takes to Build.
            </h1>
            <p style={{ color: 'var(--text-secondary)', margin: '0.3rem 0 0 0', fontSize: '1rem' }}>
              Inspect your project scope, engineering schedule, staged payment milestones, and formal sign-off.
            </p>
          </div>

          {/* Quick Lookup Input */}
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', minWidth: '320px' }}>
            <input
              type="text"
              className="workshop-input"
              placeholder="Enter Project ID (e.g. GW-REQ-1042)"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              style={{ padding: '0.6rem 1rem', fontSize: '0.9rem' }}
            />
            <button type="submit" className="btn-primary-blue" style={{ padding: '0.6rem 1.2rem', fontSize: '0.88rem' }}>
              Track
            </button>
          </form>
        </div>

        {/* Project Selector Bar (Only shown for authenticated user's own projects or admin) */}
        {allProjects.length > 0 && !id && (
          <div style={{
            backgroundColor: '#ffffff',
            padding: '0.8rem 1.2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-subtle)',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
              {currentUser?.role === 'admin' ? 'All Client Projects:' : 'Your Active Projects:'}
            </span>
            {allProjects.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setProject(p);
                  setSearchId(p.id);
                  history.push(`/project/${p.id}`);
                }}
                className={`tab-btn ${project?.id === p.id ? 'active' : ''}`}
                style={{ fontSize: '0.82rem', padding: '0.35rem 0.8rem' }}
              >
                {p.id}: {p.title.length > 25 ? `${p.title.substring(0, 25)}...` : p.title} ({p.status})
              </button>
            ))}
          </div>
        )}

        {!project ? (
          <div className="workshop-card" style={{ textAlign: 'center', padding: '5rem 1.5rem', backgroundColor: '#ffffff' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
            <h3 style={{ fontSize: '1.6rem', color: '#0f172a' }}>
              {searchId || id ? 'Project Not Found' : 'Track Your Build Proposal'}
            </h3>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '520px', margin: '0.5rem auto 1.8rem auto', lineHeight: 1.6 }}>
              {searchId || id
                ? `We couldn't find a project with the reference "${searchId || id}". Please check your tracking ID or verify your credentials.`
                : 'Enter your tracking reference (e.g. GW-REQ-8492) in the search bar above, or submit your project requirements below to receive a formal statement of work.'}
            </p>
            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/#intake" className="btn-primary-blue">
                Submit a Project Scope →
              </Link>
            </div>
          </div>
        ) : (
          <div>
            {/* Visual Lifecycle Progress Bar */}
            <div className="workshop-card" style={{ backgroundColor: '#ffffff', padding: '1.8rem 2rem', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.4rem' }}>
                <div>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    PROJECT STATUS
                  </span>
                  <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <span>{project.title}</span>
                    {project.platforms && project.platforms.length > 0 ? (
                      project.platforms.map((plat) => (
                        <span key={plat} className="pill-badge pill-blue" style={{ fontSize: '0.74rem' }}>
                          {plat}
                        </span>
                      ))
                    ) : (
                      <span className="pill-badge pill-blue" style={{ fontSize: '0.74rem' }}>
                        {project.category}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.1rem', color: 'var(--accent-blue)' }}>
                    {project.id}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Submitted {new Date(project.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Progress Steps Indicator */}
              <div className="lifecycle-progress-grid">
                {steps.map((st, idx) => {
                  const isDone = idx <= currentStepIdx;
                  const isCurrent = idx === currentStepIdx;

                  return (
                    <div key={st.key} style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        margin: '0 auto 0.5rem auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1rem',
                        backgroundColor: isDone ? '#1d4ed8' : '#f1f5f9',
                        color: isDone ? '#ffffff' : '#94a3b8',
                        fontWeight: 800,
                        border: isCurrent ? '3px solid #93c5fd' : 'none',
                        transition: 'all 0.3s ease'
                      }}>
                        {st.icon}
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        fontWeight: isDone ? 700 : 500,
                        color: isDone ? '#0f172a' : '#94a3b8',
                        lineHeight: 1.3
                      }}>
                        {st.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Clarification Alert & Reply Section */}
            {project.status === 'Needs More Info' && (
              <div style={{
                backgroundColor: '#fffbeb',
                border: '2px solid #fde68a',
                borderRadius: '16px',
                padding: '2rem',
                marginBottom: '2rem',
                boxShadow: '0 8px 24px -4px rgba(217, 119, 6, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                  <span style={{ fontSize: '1.4rem' }}>⚠️</span>
                  <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#92400e' }}>
                    Action Required: Additional Details Requested
                  </h3>
                </div>
                <p style={{ color: '#78350f', fontSize: '0.94rem', lineHeight: '1.6', marginBottom: '1.2rem' }}>
                  Guru has reviewed your build request and needs a few quick clarifications to formulate your formal Statement of Work (SOW) and cost breakdown:
                </p>

                {project.inquiryThread && project.inquiryThread.length > 0 && (
                  <div style={{ backgroundColor: '#ffffff', border: '1px solid #fde68a', borderRadius: '12px', padding: '1.4rem', marginBottom: '1.4rem' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#b45309', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                      Questions from Guru's Workshop:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#0f172a', fontSize: '0.94rem', lineHeight: '1.8' }}>
                      {project.inquiryThread[project.inquiryThread.length - 1].questions?.map((q, idx) => (
                        <li key={idx} style={{ marginBottom: '0.4rem' }}>
                          <strong>{q}</strong>
                        </li>
                      ))}
                    </ul>
                    {project.inquiryThread[project.inquiryThread.length - 1].adminNotes && (
                      <div style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px dashed #fde68a', fontSize: '0.88rem', color: '#475569' }}>
                        <strong>Note from Guru:</strong> {project.inquiryThread[project.inquiryThread.length - 1].adminNotes}
                      </div>
                    )}
                  </div>
                )}

                {/* Reply Form */}
                <form onSubmit={handleClarificationSubmit}>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#92400e', marginBottom: '0.4rem' }}>
                    Your Clarification &amp; Answers:
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={clientClarificationReply}
                    onChange={(e) => setClientClarificationReply(e.target.value)}
                    placeholder="Enter your answers or additional requirements here..."
                    style={{
                      width: '100%',
                      backgroundColor: '#ffffff',
                      border: '1.5px solid #fcd34d',
                      borderRadius: '10px',
                      padding: '0.85rem 1rem',
                      fontSize: '0.94rem',
                      outline: 'none',
                      marginBottom: '1rem',
                      color: '#0f172a',
                      lineHeight: '1.6'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={isSubmittingClarification}
                    className="btn-primary-blue"
                    style={{ backgroundColor: '#d97706', borderColor: '#d97706', padding: '0.8rem 1.6rem', fontSize: '0.92rem', fontWeight: 700 }}
                  >
                    {isSubmittingClarification ? 'Submitting Answers...' : 'Submit Answers to Guru →'}
                  </button>
                </form>
              </div>
            )}

            {/* Main Content Layout */}
            <div className="row">
              {/* Left Column: Scope & Proposal */}
              <div className="col-lg-7 mb-4 mb-lg-0">
                {/* Project Specs Summary */}
                <div className="workshop-card" style={{ padding: '2rem', backgroundColor: '#ffffff', marginBottom: '1.8rem' }}>
                  <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>💡</span> Client Vision &amp; Requirements
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.96rem', lineHeight: '1.7', whiteSpace: 'pre-line' }}>
                    {project.description}
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.4rem', paddingTop: '1.2rem', borderTop: '1px solid var(--border-subtle)', fontSize: '0.88rem' }}>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Target Completion:</span>{' '}
                      <strong>{project.timelineTarget}</strong>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Primary Contact:</span>{' '}
                      <strong>{project.client.name}</strong>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Organization:</span>{' '}
                      <strong>{project.client.organization || 'Individual Project'}</strong>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Contact Email:</span>{' '}
                      <strong>{project.client.email}</strong>
                    </div>
                  </div>
                </div>

                {/* If proposal is NOT yet published */}
                {!project.proposal ? (
                  <div className="workshop-card" style={{ padding: '2.5rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '0.8rem' }}>⚙️</div>
                    <h3 style={{ fontSize: '1.35rem', color: '#92400e', marginBottom: '0.6rem' }}>
                      Scope &amp; Timeline Under Engineering Evaluation
                    </h3>
                    <p style={{ color: '#78350f', fontSize: '0.96rem', lineHeight: '1.6', maxWidth: '520px', margin: '0 auto 1.5rem auto' }}>
                      The engineering team at Guru&apos;s Workshop is calculating the necessary technical hours, architecture topology, or timber joinery specifications. Your formal proposal with an itemized breakdown and staged payment schedule will appear here shortly.
                    </p>
                    <div style={{ fontSize: '0.84rem', color: '#b45309', fontWeight: 600 }}>
                      Avg. review turnaround: &lt; 24 business hours
                    </div>
                  </div>
                ) : (
                  /* Formal Proposal & Statement of Work */
                  <div className="workshop-card" style={{ padding: '2.5rem', backgroundColor: '#ffffff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.4rem' }}>
                      <div>
                        <div className="pill-badge pill-emerald" style={{ marginBottom: '0.6rem' }}>
                          PROPOSAL &amp; SOW READY
                        </div>
                        <h2 style={{ fontSize: '1.6rem', margin: 0, fontWeight: 800 }}>
                          What It Takes to Provide
                        </h2>
                      </div>
                      <button
                        onClick={handleDownloadSOW}
                        className="btn-outline-blue"
                        style={{ fontSize: '0.82rem', padding: '0.45rem 0.9rem' }}
                        title="Download Statement of Work specification"
                      >
                        📥 Export SOW
                      </button>
                    </div>

                    <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '1.3rem', borderRadius: '12px', border: '1px solid var(--border-subtle)', marginBottom: '1.8rem' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.4rem' }}>
                        Architectural Strategy &amp; Executive Summary
                      </div>
                      <p style={{ color: 'var(--text-primary)', margin: 0, fontSize: '0.95rem', lineHeight: '1.65' }}>
                        {project.proposal.executiveSummary}
                      </p>
                    </div>

                    <h4 style={{ fontSize: '1.1rem', marginBottom: '0.9rem' }}>Scope Deliverables:</h4>
                    <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                      {project.proposal.deliverables.map((deliv, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.65rem', fontSize: '0.93rem', color: '#334155' }}>
                          <span style={{ color: '#059669', fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>✓</span>
                          <span>{deliv}</span>
                        </li>
                      ))}
                    </ul>

                    {/* SOW Sign-Off Banner */}
                    <div style={{
                      padding: '1.5rem',
                      borderRadius: '14px',
                      backgroundColor: project.signature ? '#ecfdf5' : '#f8fafc',
                      border: project.signature ? '1px solid #a7f3d0' : '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '1rem'
                    }}>
                      <div>
                        {project.signature ? (
                          <>
                            <div style={{ color: '#059669', fontWeight: 800, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span>✓</span> SOW Digitally Executed &amp; Approved
                            </div>
                            <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: '0.2rem' }}>
                              Signed by <strong>{project.signature.legalName}</strong> &bull; {new Date(project.signature.signedAt).toLocaleString()}
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' }}>
                              Ready for Project Kickoff?
                            </div>
                            <div style={{ fontSize: '0.84rem', color: '#64748b' }}>
                              Digitally sign the formal Statement of Work to lock in your timeline and kickoff deposit.
                            </div>
                          </>
                        )}
                      </div>

                      {!project.signature && (
                        <button
                          onClick={() => setShowSignModal(true)}
                          className="btn-primary-blue"
                          style={{ padding: '0.75rem 1.6rem', fontSize: '0.92rem' }}
                        >
                          Sign SOW &amp; Approve Scope ✍️
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Staged Payment Plan & Cost Breakdown */}
              <div className="col-lg-5">
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '20px',
                  padding: '2.2rem',
                  boxShadow: 'var(--card-shadow-hover)'
                }}>
                  <div className="pill-badge pill-blue" style={{ marginBottom: '0.8rem' }}>
                    TRANSPARENT PAYMENT SCHEDULE
                  </div>
                  <h3 style={{ fontSize: '1.35rem', margin: 0, fontWeight: 800 }}>
                    Staged Milestone Plan
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', marginTop: '0.3rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                    Zero risk, zero 100% upfront lock-in. Payments are released in deliverable-backed stages.
                  </p>

                  {/* Summary Metric */}
                  <div style={{
                    padding: '1.2rem',
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '1.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Total Estimated Scope</div>
                      <div style={{ fontSize: '1.9rem', fontWeight: 900, color: 'var(--accent-blue)', fontFamily: 'Outfit' }}>
                        {project.proposal ? `$${project.proposal.totalEstimate.toLocaleString()}` : 'Calculating...'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Duration</div>
                      <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>
                        {project.proposal ? `${project.proposal.estimatedWeeks} Weeks` : 'TBD'}
                      </div>
                    </div>
                  </div>

                  {/* Milestones List */}
                  {project.proposal?.milestones ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {project.proposal.milestones.map((m, idx) => {
                        const isPaid = m.status === 'Paid';
                        const isPending = m.status === 'Pending Payment';

                        return (
                          <div
                            key={idx}
                            style={{
                              padding: '1.1rem',
                              borderRadius: '12px',
                              backgroundColor: isPaid ? '#f0fdf4' : isPending ? '#ffffff' : '#f8fafc',
                              border: isPaid ? '1px solid #86efac' : isPending ? '2px solid #1d4ed8' : '1px solid #e2e8f0',
                              boxShadow: isPending ? '0 4px 14px rgba(29, 78, 216, 0.1)' : 'none'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                              <span style={{ fontWeight: 800, fontSize: '0.94rem', color: '#0f172a' }}>
                                {m.name}
                              </span>
                              <span style={{
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                padding: '0.2rem 0.6rem',
                                borderRadius: '999px',
                                backgroundColor: isPaid ? '#dcfce7' : isPending ? '#dbeafe' : '#f1f5f9',
                                color: isPaid ? '#15803d' : isPending ? '#1d4ed8' : '#64748b'
                              }}>
                                {isPaid ? 'PAID ✅' : isPending ? 'READY TO PAY ⏳' : 'LOCKED 🔒'}
                              </span>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                              <span style={{ fontSize: '1.35rem', fontWeight: 900, color: isPaid ? '#15803d' : '#0f172a', fontFamily: 'Outfit' }}>
                                ${m.amount.toLocaleString()}
                              </span>
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                ({m.percentage}% of total)
                              </span>
                            </div>

                            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: '1.45' }}>
                              {m.trigger}
                            </p>

                            {/* Action to pay if ready */}
                            {isPending && (
                              <button
                                onClick={() => handleSimulatePayment(idx)}
                                className="btn-primary-blue"
                                style={{ width: '100%', marginTop: '0.85rem', padding: '0.55rem', fontSize: '0.86rem', justifyContent: 'center' }}
                              >
                                Pay Milestone ({`$${m.amount}`}) →
                              </button>
                            )}

                            {isPaid && m.paidAt && (
                              <div style={{ fontSize: '0.74rem', color: '#16a34a', marginTop: '0.4rem', fontWeight: 600 }}>
                                Verified on {new Date(m.paidAt).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                      Milestones will be published once technical scoping completes.
                    </div>
                  )}

                  {/* Guaranteed Protections */}
                  <div style={{ marginTop: '2rem', paddingTop: '1.2rem', borderTop: '1px solid var(--border-subtle)', fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                    <div>🛡️ <strong>Guru&apos;s Workshop Milestone Commitments:</strong></div>
                    <div>&bull; Staged release ensures you inspect every phase.</div>
                    <div>&bull; Zero third-party platform markups or hidden fees.</div>
                    <div>&bull; Direct engineering &amp; craftsmanship support.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Digital Signature Modal */}
        {showSignModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem'
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              maxWidth: '520px',
              width: '100%',
              padding: '2.5rem',
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
              color: '#0f172a'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✍️</div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.4rem' }}>
                Digital Statement of Work Execution
              </h3>
              <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: '1.5', marginBottom: '1.5rem' }}>
                Please review the terms. By executing this agreement, you authorize Guru&apos;s Workshop LLC to begin work according to the outlined milestones.
              </p>

              <form onSubmit={handleSignOff}>
                <div style={{ marginBottom: '1.2rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '0.4rem' }}>
                    Authorized Signer Full Legal Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="workshop-input"
                    placeholder="e.g. Sarah Jenkins"
                    value={legalName}
                    onChange={(e) => setLegalName(e.target.value)}
                    style={{ fontSize: '1rem', fontWeight: 600 }}
                  />
                </div>

                <div style={{
                  padding: '0.9rem',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '0.82rem',
                  color: '#475569',
                  marginBottom: '1.5rem',
                  lineHeight: '1.5'
                }}>
                  ✅ I acknowledge acceptance of the <strong>${project?.proposal?.totalEstimate}</strong> project scope, {project?.proposal?.estimatedWeeks}-week delivery window, and staged milestone payment structure.
                </div>

                <div style={{ display: 'flex', gap: '0.8rem' }}>
                  <button
                    type="button"
                    onClick={() => setShowSignModal(false)}
                    className="btn-outline-blue"
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary-blue"
                    style={{ flex: 2, justifyContent: 'center' }}
                  >
                    Confirm &amp; Sign SOW ✍️
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTracker;
